import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useId, useState } from "react";
import { Schalter } from "../design/Formular";
import { meldungsIdAnzeige, TextMitKennungen } from "../design/Kennung";
import { Kachel } from "../design/Kachel";
import { umschlag } from "../plattform/api";
import type { Zeile } from "../plattform/lesemodelle";
import { serverJetzt } from "../plattform/serverzeit";
import type { Startdialog } from "../plattform/startansicht";
import { einreihen } from "../plattform/warteschlange";
import { navigieren, positionMessen } from "./geraeteposition";
import { ansprache } from "./lage";

/**
 * Suchbereich (oberflaeche.md §4.4; design/README §10.2, Referenz `#s-nachsuche`): je zugewiesener offener Suche eine
 * `region` „<Suchart> Stand <Nr>“ mit „gefunden“, „erlegt“, „nicht gefunden“ und „Navigation“ an Stelle der
 * Hauptaktion. Das Ergebnis wirkt erst nach genau einer Bestätigung im `alertdialog` „Nachsuchenergebnis“ und ohne
 * Rückgängig (N-05, H14, §5); bei „gefunden“ und „erlegt“ markiert der Schalter „Liegeort“ (Vorgabe: an) den Fundort mit
 * der Position des Geräts. Übertragen wird über die Warteschlange (ADR-0020).
 *
 * Der Dialog steht an Stelle der Startansicht (Referenz `#s-dialog-nachsuche`): „Liegeort“ und „Abbrechen“ oben, Kopf und
 * Text darunter, „Bestätigen“ unten an der Stelle der Notruf-Kachel, 24 px unter dem Abdruck der Ergebniskacheln.
 */
const ERGEBNISSE: Array<{ begriff: string; symbol: string }> = [
  { begriff: "gefunden", symbol: "gefunden" }, { begriff: "erlegt", symbol: "erlegt" }, { begriff: "nicht gefunden", symbol: "nicht-gefunden" },
];

const bereichsname = (s: Zeile) => `${String(s["Suchart"])} Stand ${String(s["Stand"] ?? "—")}`;

export function useSuchbereiche(suchen: Zeile[]): { bereiche: React.ReactNode; dialog: Startdialog | undefined } {
  const [offen, setOffen] = useState<{ suche: Zeile; ergebnis: string } | null>(null);
  const [liegeort, setLiegeort] = useState(true);

  const bestaetigen = async () => {
    if (!offen) return;
    const { suche, ergebnis } = offen;
    const schuss = String(suche["Schuss"] ?? suche._id);
    setOffen(null);
    const daten: Record<string, unknown> = { Schuss: schuss, Nachsuchenergebnis: ergebnis, "bestätigt": true };
    const u = umschlag(daten);
    if (ergebnis !== "nicht gefunden" && liegeort) {
      const m = await positionMessen();
      if (m) Object.assign(u.daten, { Liegeort: m.position, Genauigkeit: m.genauigkeit });
    }
    await einreihen("NachsuchenergebnisMelden", `Nachsuchenergebnis zum Schuss ${schuss}`, u);
  };

  const bereiche = suchen.length ? suchen.map((s) => {
    const name = bereichsname(s);
    return (
      <section key={s._id} className="suche platte" role="region" aria-label={name}>
        <h2 className="kopf">{name}</h2>
        <span className="suche__a"><TextMitKennungen text={[ansprache(s), s["Fluchtrichtung"], s["Schuss"] ? meldungsIdAnzeige(s["Schuss"]) : null].filter(Boolean).join(" · ")} /></span>
        <div className="raster raster--2" data-gleich="">
          {ERGEBNISSE.map((e) => (
            <Kachel key={e.begriff} symbol={e.symbol} begriff={e.begriff} art="wahl"
              onPress={() => { setLiegeort(true); setOffen({ suche: s, ergebnis: e.begriff }); }} />
          ))}
          <Kachel symbol="navigation" begriff="Navigation" art="wahl" onPress={() => navigieren(s["Navigation"] as { Breite: number; Länge: number } | null)} />
        </div>
      </section>
    );
  }) : null;

  return {
    bereiche,
    dialog: offen ? {
      name: "Nachsuche", titel: "Nachsuche",
      inhalt: <Nachsuchenergebnis suche={offen.suche} ergebnis={offen.ergebnis} liegeort={liegeort} onLiegeort={setLiegeort}
        onBestaetigen={() => void bestaetigen()} onAbbrechen={() => setOffen(null)} />,
    } : undefined,
  };
}

function Nachsuchenergebnis({ suche, ergebnis, liegeort, onLiegeort, onBestaetigen, onAbbrechen }: {
  suche: Zeile; ergebnis: string; liegeort: boolean; onLiegeort: (an: boolean) => void; onBestaetigen: () => void; onAbbrechen: () => void;
}) {
  const id = useId();
  const schuss = String(suche["Schuss"] ?? suche._id);
  return (
    <div className="dialog dialog--unten" role="alertdialog" aria-labelledby={`${id}-kopf`} aria-describedby={`${id}-text`}>
      <b className="dialog__kopf platte" data-zeile-fest="" id={`${id}-kopf`}>Nachsuchenergebnis</b>
      <div className="dialog__text platte">
        <span id={`${id}-text`}>{ergebnis} · {bereichsname(suche)} · <span className="kennung">{schuss}</span></span>
        <span>{berlinUhrzeit(serverJetzt())}</span>
      </div>
      {ergebnis !== "nicht gefunden" ? <Schalter label="Liegeort" symbol="liegeort" an={liegeort} onChange={onLiegeort} /> : null}
      <Kachel symbol="schliessen" begriff="Abbrechen" art="kompakt" className="dialog__abbrechen" onPress={onAbbrechen} />
      <Kachel symbol="haken" begriff="Bestätigen" art="kern" onPress={onBestaetigen} />
    </div>
  );
}
